import api from './api';

/**
 * Fetch all packages with optional filters
 * @param {Object} params - Query parameters (page, page_size, status, user, etc.)
 * @returns {Promise} Packages data
 */
export const fetchPackages = async (params = {}) => {
  const queryParams = new URLSearchParams();
  
  if (params.page) queryParams.append('page', params.page);
  if (params.page_size) queryParams.append('page_size', params.page_size);
  if (params.status) queryParams.append('packaging_status', params.status);
  if (params.user) queryParams.append('user', params.user);
  if (params.search) queryParams.append('search', params.search);
  if (params.in_pool) queryParams.append('in_pool', params.in_pool);
  
  const res = await api.get(`/admin/packages/?${queryParams.toString()}`);
  return res.data;
};

/**
 * Fetch single package by ID
 * @param {number} packageId - Package ID
 * @returns {Promise} Package data
 */
export const fetchPackageById = async (packageId) => {
  const res = await api.get(`/admin/packages/${packageId}/`);
  return res.data;
};

/**
 * Upload package information (photos, weight, CBM)
 * @param {number} packageId - Package ID
 * @param {Object} data - Package info (package_photos, package_weight, package_cbm)
 * @returns {Promise} Response data
 */
export const uploadPackageInfo = async (packageId, data) => {
  const res = await api.post(`/admin/packages/${packageId}/upload-package-info/`, data);
  return res.data;
};

export const deletePackagePhoto = async (packageId, photoUrl) => {
  const res = await api.post(`/admin/packages/${packageId}/delete-package-photo/`, {
    photo_url: photoUrl
  });
  return res.data;
};

/**
 * Mark package as shipped
 * @param {number} packageId - Package ID
 * @returns {Promise} Response data
 */
export const shipPackage = async (packageId) => {
  const res = await api.post(`/admin/packages/${packageId}/ship/`);
  return res.data;
};

/**
 * Record that a shipped package is available for customer pickup.
 * The backend automatically marks it picked up after seven days unless the
 * customer confirms pickup earlier.
 */
export const markPackagePickupAvailable = async (packageId) => {
  const res = await api.post(`/admin/packages/${packageId}/mark-pickup-available/`);
  return res.data;
};

export const resendPackagePickupMessage = async (packageId) => {
  const res = await api.post(`/admin/packages/${packageId}/resend-pickup-message/`);
  return res.data;
};

export const bulkMarkPackagesPickedUpSilently = async (packageIds) => {
  const res = await api.post('/admin/packages/bulk-mark-picked-up-silent/', {
    package_ids: packageIds
  });
  return res.data;
};

/**
 * Update package
 * @param {number} packageId - Package ID
 * @param {Object} data - Package data to update
 * @returns {Promise} Updated package data
 */
export const updatePackage = async (packageId, data) => {
  const res = await api.patch(`/admin/packages/${packageId}/`, data);
  return res.data;
};

export const updatePackageLogisticsFees = async (packageId, data) => {
  const res = await api.patch(`/admin/packages/${packageId}/logistics-fees/`, data);
  return res.data;
};

export const deletePackage = async (packageId) => {
  await api.delete(`/admin/packages/${packageId}/`);
  return true;
};

/**
 * Split a package by moving selected orders to a new package
 * @param {number} packageId - Package ID
 * @param {array} orderIds - Array of order IDs to move to new package
 * @returns {Promise} Response data with both packages
 */
export const splitPackage = async (packageId, orderIds) => {
  const res = await api.post(`/admin/packages/${packageId}/split-package/`, {
    order_ids: orderIds
  });
  return res.data;
};

/**
 * Auto-split a package by goods type (normal vs special)
 * @param {number} packageId - Package ID
 * @returns {Promise} Response data with both packages
 */
export const splitPackageByGoodsType = async (packageId) => {
  const res = await api.post(`/admin/packages/${packageId}/split-by-goods-type/`, {});
  return res.data;
};

/**
 * Merge multiple packages into one
 * @param {array} sourcePackageIds - Array of package IDs to merge
 * @param {number} targetPackageId - Optional target package ID
 * @returns {Promise} Response data with merged package
 */
export const mergePackages = async (sourcePackageIds, targetPackageId = null) => {
  const payload = { source_package_ids: sourcePackageIds };
  if (targetPackageId) {
    payload.target_package_id = targetPackageId;
  }
  
  const res = await api.post(`/admin/packages/merge-packages/`, payload);
  return res.data;
};

/**
 * Update package status
 * @param {number} packageId - Package ID
 * @param {string} status - New packaging status
 * @returns {Promise} Response data
 */
export const updatePackageStatus = async (packageId, status) => {
  const res = await api.post(`/admin/packages/${packageId}/update-status/`, {
    packaging_status: status
  });
  return res.data;
};

/**
 * Reply to a package dispute
 * @param {number} packageId - Package ID
 * @param {string} message - Reply message
 * @param {array} images - Optional array of image URLs
 * @returns {Promise} Response data
 */
export const replyDispute = async (packageId, message, images = []) => {
  const res = await api.post(`/admin/packages/${packageId}/reply-dispute/`, {
    message,
    images
  });
  return res.data;
};

/**
 * Resolve a package dispute
 * @param {number} packageId - Package ID
 * @param {string} resolutionNotes - Admin notes on resolution
 * @param {string} message - Optional final message to user
 * @param {array} images - Optional array of image URLs
 * @returns {Promise} Response data
 */
export const resolveDispute = async (packageId, resolutionNotes, message = null, images = []) => {
  const payload = { resolution_notes: resolutionNotes };
  if (message) payload.message = message;
  if (images.length > 0) payload.images = images;
  
  const res = await api.post(`/admin/packages/${packageId}/resolve-dispute/`, payload);
  return res.data;
};

/**
 * Distribute shipping costs for a shared package
 * @param {number} packageId - Package ID
 * @param {number} totalShippingCost - Total shipping cost (International + Local)
 * @param {string} dueDate - Optional payment due date (ISO string)
 * @returns {Promise} Response data
 */
export const distributeShippingCosts = async (packageId, totalShippingCost, dueDate = null) => {
  const payload = {
    total_shipping_cost: totalShippingCost
  };
  
  if (dueDate) {
    payload.payment_due_date = dueDate;
  }
  
  const res = await api.post(`/admin/packages/${packageId}/distribute-shipping-costs/`, payload);
  return res.data;
};

export const searchAssistedPackages = async (params = {}) => {
  const queryParams = new URLSearchParams();

  if (params.page) queryParams.append('page', params.page);
  if (params.page_size) queryParams.append('page_size', params.page_size);
  if (params.search) queryParams.append('search', params.search);
  if (params.user) queryParams.append('user', params.user);
  if (params.packaging_status) queryParams.append('packaging_status', params.packaging_status);

  const res = await api.get(`/admin/packages/assisted/search/?${queryParams.toString()}`);
  return res.data;
};

export const assistedSelectShipping = async (packageId, payload) => {
  const res = await api.post(`/admin/packages/${packageId}/assisted-select-shipping/`, payload);
  return res.data;
};

export const assistedPayPackage = async (packageId, payload) => {
  const res = await api.post(`/admin/packages/${packageId}/assisted-pay/`, payload);
  return res.data;
};

export const fetchTransferPaymentPreview = async (packageId) => {
  const res = await api.get(`/admin/packages/${packageId}/transfer-payment-preview/`);
  return res.data;
};

export const payPackageWithTransfer = async (packageId, payload) => {
  const formData = new FormData();
  formData.append('confirm_received', payload.confirm_received ? 'true' : 'false');
  formData.append('receipt', payload.receipt);
  if (payload.note !== undefined) {
    formData.append('note', payload.note);
  }

  const res = await api.post(`/admin/packages/${packageId}/pay-with-transfer/`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return res.data;
};

/**
 * Get count of packages with open disputes
 * @returns {Promise<number>} Count of packages with open disputes
 */
export const getOpenDisputesCount = async () => {
  try {
    // Fetch packages and filter for those with open disputes
    const res = await api.get('/admin/packages/?page_size=1000');
    const packages = res.data.results || [];
    const openDisputesCount = packages.filter(
      pkg => pkg.dispute && pkg.dispute.status === 'open'
    ).length;
    return openDisputesCount;
  } catch (error) {
    console.error('Error fetching open disputes count:', error);
    return 0;
  }
};

const packageService = {
  fetchPackages,
  fetchPackageById,
  uploadPackageInfo,
  shipPackage,
  markPackagePickupAvailable,
  bulkMarkPackagesPickedUpSilently,
  updatePackage,
  updatePackageLogisticsFees,
  deletePackage,
  splitPackage,
  splitPackageByGoodsType,
  mergePackages,
  updatePackageStatus,
  resolveDispute,
  distributeShippingCosts,
  searchAssistedPackages,
  assistedSelectShipping,
  assistedPayPackage,
  fetchTransferPaymentPreview,
  payPackageWithTransfer,
  getOpenDisputesCount
};

export default packageService;
